import {
  ApproveContactChangeInput,
  KybContactChangeRequest,
  KybContactChangeStatus,
  KybContactMfaChallenge,
  RejectContactChangeInput,
  SaveContactInput,
  SaveContactOutcome,
} from "@/core/kyb/entities/KybContact";
import { DefaultHttpExceptionType } from "http-repository-cache";
import Exception from "@/core/exceptions/Exception";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import EnvKeys from "@/core/env/EnvKeys";
import ViteEnv from "@/details/env/vite/ViteEnv";
import { useAuthStore } from "@/core/stores/authStore";
import KybRepository from "@/core/kyb/repositories/KybRepository";
import {
  EntrepriseIdentification,
  Immatriculation,
  ImmatriculationStatus,
  TaxIdentifier,
  RegistrationVerification,
} from "@/core/kyb/entities/EntrepriseIdentification";
import {
  RegistrationProofUploadResult,
  RegistrationVerificationDecisionInput,
  RegistrationVerificationDecisionResult,
} from "@/core/kyb/entities/RegistrationVerification";
import {
  AddRepresentativeCommand,
  AddRepresentativeOutcome,
  KybPersonCandidate,
  KybPersonCandidateMatchType,
  KybPersonCandidateOrigin,
  KybPersonRole,
  KybRelationRole,
  KybRelationRoleCategory,
  KybRelationScreen,
  KybRepresentativePerson,
  KybRepresentativeSource,
  KybRoleVerificationStatus,
  KybScreeningOutcome,
  ScreeningRelaunchOutcome,
  ScreeningRelaunchSummary,
} from "@/core/kyb/entities/KybRepresentative";
import {
  AddLegalPartyCommand,
  AddOwnershipEdgeCommand,
  KybBeneficialOwner,
  KybBeneficialOwnerOrigin,
  KybDirectorFallback,
  KybGraphExportFile,
  KybGraphExportFormat,
  KybLegalParty,
  KybOpacityAssessment,
  KybOpacityLevel,
  KybOwnershipEdge,
  KybOwnershipNature,
  KybOwnershipSource,
  KybPartyType,
} from "@/core/kyb/entities/KybOwnership";
import {
  DocumentChecklist,
  DocumentChecklistItem,
  CaseDocumentStatus,
  CaseDocumentUploadResult,
  DocumentObligation,
} from "@/core/kyb/entities/DocumentChecklist";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeKybRequest,
  executeKybVoidRequest,
  toKybException,
} from "../../http/services/kybRequest/kybRequest";
import kybEndpoints from "../../api/kybEndpoints/kybEndpoints";
import { updateCustomerKYCUrl } from "../../api/customersBackendEndpoints/customersBackendEndpoints";
import { lookup } from "@/utils/lookup";

/** Bloc `legalEntity` du PATCH `/customers/prospect/{businessKey}` — mise à jour partielle, seuls
 * les champs fournis (non `undefined`) sont envoyés. */
type LegalEntityDetailsPatchDto = {
  shareCapital?: number;
  incorporationDate?: string;
  headOfficeAddress?: string;
  corporatePurpose?: string;
  sectorActivityCode?: string;
  tradeName?: string;
  city?: string;
  authorizationNumber?: string;
  fiscalYearStart?: string;
  fiscalYearEnd?: string;
  annualRevenue?: number;
  consolidatedRevenue?: number;
  netResult?: number;
  totalAssets?: number;
  companyEmail?: string;
  companyPhone?: string;
};

type KybDirectorFallbackDto = {
  fallbackId: string;
  relatedPersonId: string;
  directorLabel: string | null;
  justification: string;
  status: KybDirectorFallback["status"];
  statusLabel: string | null;
  covering: boolean;
  submittedBy: string | null;
  submittedAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
};

function mapDirectorFallback(dto: KybDirectorFallbackDto): KybDirectorFallback {
  return {
    fallbackId: dto.fallbackId,
    relatedPersonId: dto.relatedPersonId,
    directorLabel: dto.directorLabel ?? undefined,
    justification: dto.justification,
    status: dto.status,
    statusLabel: dto.statusLabel ?? undefined,
    covering: dto.covering,
    submittedBy: dto.submittedBy ?? undefined,
    submittedAt: dto.submittedAt ?? undefined,
    decidedBy: dto.decidedBy ?? undefined,
    decidedAt: dto.decidedAt ?? undefined,
    decisionReason: dto.decisionReason ?? undefined,
  };
}

type KybLegalEntityCaseDto = {
  caseId: string;
};

type KybCaseDocumentDto = {
  caseDocumentId: string;
  caseId: string;
  typePieceCode: string;
  obligation: DocumentObligation;
  quantiteAttendue: number;
  baseReglementaire: string;
  statut: CaseDocumentStatus;
  satisfaite: boolean;
  documentId: string | null;
  dateExpiration: string | null;
  commentaire: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
};

type KybCaseDocumentChecklistDto = {
  determine: boolean;
  complet: boolean;
  tauxCompletude: number;
  nombreAttendu: number;
  nombreSatisfait: number;
  pieces: KybCaseDocumentDto[];
  manquantes: KybCaseDocumentDto[];
  expirees: KybCaseDocumentDto[];
};

function mapChecklistItem(dto: KybCaseDocumentDto): DocumentChecklistItem {
  return {
    caseDocumentId: dto.caseDocumentId,
    typePieceCode: dto.typePieceCode,
    obligation: dto.obligation,
    quantiteAttendue: dto.quantiteAttendue,
    baseReglementaire: dto.baseReglementaire,
    statut: dto.statut,
    satisfaite: dto.satisfaite,
    documentId: dto.documentId ?? undefined,
    dateExpiration: dto.dateExpiration ?? undefined,
    commentaire: dto.commentaire ?? undefined,
    updatedBy: dto.updatedBy ?? undefined,
    updatedAt: dto.updatedAt ?? undefined,
  };
}

function mapChecklist(
  businessKey: string,
  dto: KybCaseDocumentChecklistDto,
): DocumentChecklist {
  return {
    businessKey,
    determine: dto.determine,
    complet: dto.complet,
    tauxCompletude: dto.tauxCompletude,
    pieces: dto.pieces.map(mapChecklistItem),
  };
}

/** Miroir de `KybRelatedPersonDocumentResponse` (dépôt niveau dossier, écran 05). */
type KybCaseDocumentUploadDto = {
  personDocumentId: string;
  typePieceCode: string;
  fileName: string;
};

type KybEntityRegistrationDto = {
  registrationId: string;
  idType: string;
  idValue: string;
  countryCode: string;
  issuedDate: string | null;
  verificationStatus: ImmatriculationStatus;
  /** Identifiant projeté du RIC : il ne se corrige pas ici. */
  lectureSeule?: boolean;
  verificationSource?: string | null;
  verificationJustification?: string | null;
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  proofDocumentId?: string | null;
};

/** Contenu de la décision de vérification, ou rien tant qu'aucune décision n'a été prise. */
const toVerification = (dto: KybEntityRegistrationDto): RegistrationVerification | undefined => {
  const verification: RegistrationVerification = {
    source: dto.verificationSource ?? undefined,
    justification: dto.verificationJustification ?? undefined,
    verifiedBy: dto.verifiedBy ?? undefined,
    verifiedAt: dto.verifiedAt ?? undefined,
    proofDocumentId: dto.proofDocumentId ?? undefined,
  };
  return Object.values(verification).some((value) => value !== undefined) ? verification : undefined;
};

type KybEntityRegistrationProofDto = {
  proofDocumentId: string;
  sha256: string;
  fileName: string;
};

type KybEntityRegistrationVerifyResponseDto = {
  verificationStatus: ImmatriculationStatus;
  verificationSource: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
};

const registrationKey = (idType: string, idValue: string): string =>
  `${idType}|${idValue}`;

type KybRelationRoleDto = {
  code: string;
  label: string;
  category: KybRelationRoleCategory;
  categoryLabel: string;
  partieLieeType: string | null;
};

function mapRelationRole(dto: KybRelationRoleDto): KybRelationRole {
  return {
    code: dto.code,
    label: dto.label,
    category: dto.category,
    categoryLabel: dto.categoryLabel,
    relatedPartyNature:
      (dto.partieLieeType as KybRelationRole["relatedPartyNature"]) ??
      undefined,
  };
}

type KybRepresentativeDto = {
  relatedPersonId: string;
  roleCode: string;
  roleLabel: string | null;
  roleCategory: "DIRIGEANT" | "SIGNATAIRE";
  lastName: string;
  firstName: string;
  birthDate: string | null;
  nationalityCode: string | null;
  professionCode: string | null;
  personCustomerId: string | null;
  email?: string | null;
  phone?: string | null;
  effectiveEmail?: string | null;
  effectivePhone?: string | null;
  contactSource?: "LOCAL" | "RIC" | null;
  source: KybRepresentativeSource | null;
  screeningOutcome: KybScreeningOutcome | null;
  projectedAt: string | null;
  createdAt: string;
};

type KybContactChangeDto = {
  requestId: string;
  relatedPersonId: string;
  proposedEmail: string | null;
  proposedPhone: string | null;
  status: KybContactChangeStatus;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
};

function mapContactChange(dto: KybContactChangeDto): KybContactChangeRequest {
  return {
    requestId: dto.requestId,
    relatedPersonId: dto.relatedPersonId,
    proposedEmail: dto.proposedEmail ?? undefined,
    proposedPhone: dto.proposedPhone ?? undefined,
    status: dto.status,
    requestedBy: dto.requestedBy,
    requestedAt: dto.requestedAt,
    decidedBy: dto.decidedBy ?? undefined,
    decidedAt: dto.decidedAt ?? undefined,
    decisionComment: dto.decisionComment ?? undefined,
  };
}

function mapRepresentative(dto: KybRepresentativeDto): KybRepresentativePerson {
  return {
    relatedPersonId: dto.relatedPersonId,
    roleCode: dto.roleCode,
    roleLabel: dto.roleLabel ?? undefined,
    roleCategory: dto.roleCategory,
    lastName: dto.lastName,
    firstName: dto.firstName,
    birthDate: dto.birthDate ?? undefined,
    nationalityCode: dto.nationalityCode ?? undefined,
    professionCode: dto.professionCode ?? undefined,
    personCustomerId: dto.personCustomerId ?? undefined,
    email: dto.email ?? undefined,
    phone: dto.phone ?? undefined,
    effectiveEmail: dto.effectiveEmail ?? undefined,
    effectivePhone: dto.effectivePhone ?? undefined,
    contactSource: dto.contactSource ?? undefined,
    source: dto.source ?? "AGENT_INPUT",
    screeningOutcome: dto.screeningOutcome ?? undefined,
    projectedAt: dto.projectedAt ?? undefined,
    createdAt: dto.createdAt,
  };
}

type KybPersonCandidateDto = {
  origin: KybPersonCandidateOrigin;
  matchType: KybPersonCandidateMatchType;
  relatedPersonId: string | null;
  personCustomerId: string | null;
  lastName: string;
  firstName: string;
  birthDate: string | null;
  nationalityCode: string | null;
  screeningOutcome: KybScreeningOutcome | null;
  roleCodes: string[];
};

function mapPersonCandidate(dto: KybPersonCandidateDto): KybPersonCandidate {
  return {
    origin: dto.origin,
    matchType: dto.matchType,
    relatedPersonId: dto.relatedPersonId ?? undefined,
    personCustomerId: dto.personCustomerId ?? undefined,
    lastName: dto.lastName,
    firstName: dto.firstName,
    birthDate: dto.birthDate ?? undefined,
    nationalityCode: dto.nationalityCode ?? undefined,
    screeningOutcome: dto.screeningOutcome ?? undefined,
    roleCodes: dto.roleCodes,
  };
}

type KybPersonRoleDto = {
  personRoleId: string;
  relatedPersonId: string;
  roleCode: string;
  roleLabel: string | null;
  roleCategory: "DIRIGEANT" | "SIGNATAIRE" | null;
  verificationStatus: KybRoleVerificationStatus;
  createdAt: string;
};

function mapPersonRole(dto: KybPersonRoleDto): KybPersonRole {
  return {
    personRoleId: dto.personRoleId,
    relatedPersonId: dto.relatedPersonId,
    roleCode: dto.roleCode,
    roleLabel: dto.roleLabel ?? undefined,
    roleCategory: dto.roleCategory ?? undefined,
    verificationStatus: dto.verificationStatus,
    createdAt: dto.createdAt,
  };
}

type KybLegalPartyDto = {
  relatedPersonId: string;
  roleCode: string;
  roleLabel: string | null;
  denomination: string;
  legalFormCode: string | null;
  registrationId: string | null;
  countryCode: string | null;
  source: KybOwnershipSource | null;
  createdAt: string;
};

function mapLegalParty(dto: KybLegalPartyDto): KybLegalParty {
  return {
    relatedPersonId: dto.relatedPersonId,
    roleCode: dto.roleCode,
    roleLabel: dto.roleLabel ?? undefined,
    denomination: dto.denomination,
    legalFormCode: dto.legalFormCode ?? undefined,
    registrationId: dto.registrationId ?? undefined,
    countryCode: dto.countryCode ?? undefined,
    source: dto.source ?? "AGENT_INPUT",
    createdAt: dto.createdAt,
  };
}

type KybOwnershipEdgeDto = {
  ownershipEdgeId: string;
  ownerPartyId: string;
  ownerLabel: string | null;
  ownerPartyType: KybPartyType | null;
  ownedPartyId: string | null;
  ownedLabel: string | null;
  rootTarget: boolean;
  nature: KybOwnershipNature | null;
  percentage: number | null;
  controlBasis: string | null;
  source: KybOwnershipSource | null;
  createdAt: string;
};

function mapOwnershipEdge(dto: KybOwnershipEdgeDto): KybOwnershipEdge {
  return {
    ownershipEdgeId: dto.ownershipEdgeId,
    ownerPartyId: dto.ownerPartyId,
    ownerLabel: dto.ownerLabel ?? undefined,
    ownerPartyType: dto.ownerPartyType ?? undefined,
    ownedPartyId: dto.ownedPartyId ?? undefined,
    ownedLabel: dto.ownedLabel ?? undefined,
    rootTarget: dto.rootTarget,
    // La nature est garantie non nulle par le back (400 sinon à la création) ; le typage optionnel
    // du DTO ne couvre que la robustesse de lecture, pas un état réellement atteignable.
    nature: dto.nature ?? "CAPITAL",
    percentage: dto.percentage ?? undefined,
    controlBasis: dto.controlBasis ?? undefined,
    source: dto.source ?? "AGENT_INPUT",
    createdAt: dto.createdAt,
  };
}

type KybBeneficialOwnerDto = {
  partyId: string;
  displayName: string | null;
  origin: KybBeneficialOwnerOrigin;
  originLabel: string | null;
  effectiveCapitalPercent: number | null;
  effectiveVotePercent: number | null;
  controlBasis: string | null;
  thresholdPercent: number;
};

function mapBeneficialOwner(dto: KybBeneficialOwnerDto): KybBeneficialOwner {
  return {
    partyId: dto.partyId,
    displayName: dto.displayName ?? undefined,
    origin: dto.origin,
    originLabel: dto.originLabel ?? undefined,
    effectiveCapitalPercent: dto.effectiveCapitalPercent ?? undefined,
    effectiveVotePercent: dto.effectiveVotePercent ?? undefined,
    controlBasis: dto.controlBasis ?? undefined,
    thresholdPercent: dto.thresholdPercent,
  };
}

type KybOpacityContributionDto = {
  indicator: string;
  indicatorLabel: string | null;
  detected: boolean;
  measure: number;
  weight: number;
  contribution: number;
  detail: string | null;
};

type KybOpacityAssessmentDto = {
  caseId: string;
  score: number;
  level: KybOpacityLevel;
  levelLabel: string | null;
  eddRecommended: boolean;
  eddThreshold: number;
  contributions: KybOpacityContributionDto[];
};

function mapOpacityAssessment(dto: KybOpacityAssessmentDto): KybOpacityAssessment {
  return {
    caseId: dto.caseId,
    score: dto.score,
    level: dto.level,
    levelLabel: dto.levelLabel ?? undefined,
    eddRecommended: dto.eddRecommended,
    eddThreshold: dto.eddThreshold,
    contributions: dto.contributions.map((c) => ({
      indicator: c.indicator,
      indicatorLabel: c.indicatorLabel ?? undefined,
      detected: c.detected,
      measure: c.measure,
      weight: c.weight,
      contribution: c.contribution,
      detail: c.detail ?? undefined,
    })),
  };
}

/**
 * AC-2 : le back renvoie un 409 avec les candidats dans `data` quand un doublon est détecté.
 * `DefaultHttpClient` reconnaît ce cas ({@link HttpClient} → type `CONFLICT`) et rejette avec
 * `{ code, message, data, type: "CONFLICT" }` — extrait la liste si c'est bien cette forme,
 * `null` sinon pour laisser le chemin d'erreur générique gérer le reste (dossier clos, etc.,
 * qui sont aussi des 409 mais sans candidats à proposer).
 */
function extractDuplicateCandidates(error: unknown): KybPersonCandidateDto[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const { type, data } = error as { type?: string; data?: unknown };
  if (type !== "CONFLICT" || !Array.isArray(data)) {
    return null;
  }
  return data as KybPersonCandidateDto[];
}

// Le back persiste immatriculations et identifiants fiscaux dans la même ressource
// (kyb_entity_registration, un seul `idType`) ; le front les répartit en deux sections
// distinctes (ImmatriculationsSection / TaxIdentifiersSection). "AUTRE" existe des deux
// côtés dans l'UI : à l'hydratation, seuls les types fiscaux connus vont dans les
// identifiants fiscaux, tout le reste (RCCM, AUTRE, type inconnu) reste une immatriculation.
const TAX_IDENTIFIER_ID_TYPES = new Set(["IFU", "NIF", "NINEA"]);

/** Type d'exception d'un refus d'export, par un branchement explicite (règle detect-object-injection). */
function exportExceptionType(status: number): DefaultExceptionTypes {
  switch (status) {
    case 400:
      return DefaultExceptionTypes.BAD_REQUEST;
    case 401:
      return DefaultExceptionTypes.UNAUTHORIZED;
    case 403:
      return DefaultExceptionTypes.FORBIDDEN;
    case 404:
      return DefaultExceptionTypes.NOT_FOUND;
    case 409:
      return DefaultExceptionTypes.CONFLICT;
    default:
      return DefaultExceptionTypes.UNKNOWN_ERROR;
  }
}

/** Refus de l'export : le message du back, suivi des points à résoudre quand il en liste (409). */
async function exportException(response: Response): Promise<Exception<unknown, DefaultExceptionTypes>> {
  const body = (await response.json().catch(() => null)) as { message?: string; data?: unknown } | null;
  const points = Array.isArray(body?.data) ? (body?.data as unknown[]).map(String) : [];
  const message = [body?.message, points.length > 0 ? points.join(" ; ") : undefined].filter(Boolean).join(" : ");
  return {
    type: exportExceptionType(response.status),
    message: message || undefined,
  };
}

/** Nom de fichier proposé par le back (`Content-Disposition: attachment; filename=...`), sinon un repli. */
function fileNameOf(disposition: string | null, fallback: string): string {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1]?.trim() || fallback;
}

function timestamp(): string {
  return new Date().toISOString().replaceAll(/[-:T]/g, "").slice(0, 14);
}

export default class KybRepositoryBackend implements KybRepository {
  constructor(private readonly httpClient: HttpClient) {}

  private async fetchExistingRegistrations(
    caseId: string,
  ): Promise<Map<string, KybEntityRegistrationDto>> {
    const response = await this.httpClient.get<
      ApiResponse<KybEntityRegistrationDto[]>
    >({
      url: kybEndpoints.entityRegistrations(caseId),
      withAuth: true,
    });
    const byKey = new Map<string, KybEntityRegistrationDto>();
    for (const dto of response.data) {
      byKey.set(registrationKey(dto.idType, dto.idValue), dto);
    }
    return byKey;
  }

  private async addRegistration(
    caseId: string,
    idType: string,
    idValue: string,
    countryCode: string,
    issuedDate?: string,
  ): Promise<KybEntityRegistrationDto> {
    const response = await this.httpClient.post<
      ApiResponse<KybEntityRegistrationDto>,
      { idType: string; idValue: string; countryCode: string; issuedDate?: string }
    >({
      url: kybEndpoints.entityRegistrations(caseId),
      body: { idType, idValue, countryCode, issuedDate },
      withAuth: true,
    });
    return response.data;
  }

  /** Corrige la date d'émission d'une immatriculation déjà enregistrée : sans cela, une date saisie après coup était perdue. */
  private async updateRegistrationDate(
    caseId: string,
    registrationId: string,
    issuedDate: string,
  ): Promise<KybEntityRegistrationDto> {
    const response = await this.httpClient.patch<ApiResponse<KybEntityRegistrationDto>, { issuedDate: string }>({
      url: kybEndpoints.entityRegistration(caseId, registrationId),
      body: { issuedDate },
      withAuth: true,
    });
    return response.data;
  }

  /**
   * Ajoute au dossier réel toute immatriculation pas encore synchronisée avec le back (#24424) ;
   * une ligne déjà présente (même idType + idValue) est réutilisée telle quelle. Le statut de
   * vérification n'est jamais écrit ici : il vient uniquement du back (NON_VERIFIE à la création,
   * décidé ensuite par la conformité sur une ressource dédiée, avec preuve).
   */
  private async syncImmatriculations(
    caseId: string,
    rows: Immatriculation[],
    existing: Map<string, KybEntityRegistrationDto>,
  ): Promise<Immatriculation[]> {
    const result: Immatriculation[] = [];
    for (const row of rows) {
      const { registryType: idType, number: idValue, country } = row;
      if (!idType || !idValue || !country) {
        result.push({ ...row, status: "NON_VERIFIE" });
        continue;
      }
      const key = registrationKey(idType, idValue);
      let dto = existing.get(key);
      if (!dto) {
        dto = await this.addRegistration(caseId, idType, idValue, country, row.date);
        existing.set(key, dto);
      } else if (row.date && row.date !== dto.issuedDate && !dto.lectureSeule) {
        dto = await this.updateRegistrationDate(caseId, dto.registrationId, row.date);
        existing.set(key, dto);
      }
      result.push({
        ...row,
        registrationId: dto.registrationId,
        date: dto.issuedDate ?? row.date,
        status: dto.verificationStatus,
        verification: toVerification(dto),
      });
    }
    return result;
  }

  /** Même logique que {@link syncImmatriculations}, pour les identifiants fiscaux (NIF/IFU/NINEA/AUTRE). */
  private async syncTaxIdentifiers(
    caseId: string,
    rows: TaxIdentifier[],
    existing: Map<string, KybEntityRegistrationDto>,
  ): Promise<TaxIdentifier[]> {
    const result: TaxIdentifier[] = [];
    for (const row of rows) {
      const { type: idType, value: idValue, country } = row;
      if (!idType || !idValue || !country) {
        result.push({ ...row, status: "NON_VERIFIE" });
        continue;
      }
      const key = registrationKey(idType, idValue);
      let dto = existing.get(key);
      if (!dto) {
        dto = await this.addRegistration(caseId, idType, idValue, country);
        existing.set(key, dto);
      }
      result.push({
        ...row,
        registrationId: dto.registrationId,
        status: dto.verificationStatus,
        verification: toVerification(dto),
      });
    }
    return result;
  }

  /**
   * Le dossier KYB (case) est le porteur de la checklist documentaire, des représentants et de
   * l'actionnariat côté back — mais le front (comme le reste de l'app) ne connaît que le
   * businessKey. L'ouverture est idempotente (#24423) : un rejeu renvoie le dossier existant, donc
   * appeler ceci à chaque action au lieu de mettre le caseId en cache ne crée jamais de doublon et
   * évite un état de session supplémentaire à invalider.
   */
  private async resolveCaseId(businessKey: string): Promise<string> {
    // Une clé vide est un défaut de l'appelant (état de l'écran perdu) : on ne la poste pas au backend, qui répondrait
    // une erreur de validation technique sans rapport avec ce que l'agent a fait.
    if (businessKey.trim() === "") {
      throw new Error("Impossible de résoudre le dossier KYB : la clé métier du client est vide.");
    }
    const response = await this.httpClient.post<
      ApiResponse<KybLegalEntityCaseDto>,
      { businessKey: string }
    >({
      url: kybEndpoints.openOrGetCase(),
      body: { businessKey },
      withAuth: true,
    });
    return response.data.caseId;
  }

  /**
   * Bloc `legalEntity` du PATCH prospect (#26005, point 1) : capital, date de constitution, adresse
   * du siège, objet social, secteur d'activité, nom commercial, ville — tous les champs de l'écran
   * Entreprise qui n'ont pas déjà leur propre canal de persistance (immatriculations/taxIdentifiers
   * via le dossier KYB ; raison sociale/téléphone/email/pays/forme juridique verrouillés, saisis à
   * l'écran 01). N'envoie que les champs renseignés (mise à jour partielle, comme le contrat back).
   */
  private buildLegalEntityPatch(
    data: EntrepriseIdentification,
  ): LegalEntityDetailsPatchDto | null {
    // champ de l'écran -> champ du PATCH ; les textes partent s'ils sont renseignés, les montants dès qu'ils sont saisis (0 compris)
    const textFields: ReadonlyArray<readonly [keyof EntrepriseIdentification, string]> = [
      ["incorporationDate", "incorporationDate"],
      ["headOfficeAddress", "headOfficeAddress"],
      ["corporateObject", "corporatePurpose"],
      ["activitySector", "sectorActivityCode"],
      ["tradeName", "tradeName"],
      ["city", "city"],
      ["authorizationNumber", "authorizationNumber"],
      ["fiscalYearStart", "fiscalYearStart"],
      ["fiscalYearEnd", "fiscalYearEnd"],
      ["companyEmail", "companyEmail"],
      ["companyPhone", "companyPhone"],
    ];
    const amountFields: ReadonlyArray<readonly [keyof EntrepriseIdentification, string]> = [
      ["shareCapital", "shareCapital"],
      ["annualRevenue", "annualRevenue"],
      ["consolidatedRevenue", "consolidatedRevenue"],
      ["netResult", "netResult"],
      ["totalAssets", "totalAssets"],
    ];
    const patch: Record<string, unknown> = {};
    for (const [from, to] of textFields) {
      const value = lookup(data, from);
      if (value) {
        Object.assign(patch, { [to]: value });
      }
    }
    for (const [from, to] of amountFields) {
      const value = lookup(data, from);
      if (value !== undefined && value !== null) {
        Object.assign(patch, { [to]: value });
      }
    }
    return Object.keys(patch).length > 0 ? (patch as LegalEntityDetailsPatchDto) : null;
  }

  private async syncCompanyDetails(
    businessKey: string,
    data: EntrepriseIdentification,
  ): Promise<void> {
    const legalEntity = this.buildLegalEntityPatch(data);
    if (!legalEntity) {
      return;
    }
    await this.httpClient.patch<ApiResponse<unknown>, { legalEntity: LegalEntityDetailsPatchDto }>({
      url: updateCustomerKYCUrl(businessKey),
      body: { legalEntity },
      withAuth: true,
    });
  }

  /**
   * Immatriculations et identifiants fiscaux (#24424) sont persistés via le dossier KYB. Les
   * détails d'entreprise (capital, date, adresse, objet, secteur, nom commercial, ville) sont
   * persistés via `PATCH /customers/prospect/{businessKey}` (#26005, point 1, livré le même jour
   * que ce câblage).
   */
  async saveEntrepriseIdentification(
    businessKey: string,
    data: EntrepriseIdentification,
  ): Promise<RepositoryResult<EntrepriseIdentification>> {
    try {
      const caseId = await this.resolveCaseId(businessKey);
      const existing = await this.fetchExistingRegistrations(caseId);
      const immatriculations = await this.syncImmatriculations(
        caseId,
        data.immatriculations,
        existing,
      );
      const taxIdentifiers = await this.syncTaxIdentifiers(
        caseId,
        data.taxIdentifiers,
        existing,
      );
      await this.syncCompanyDetails(businessKey, data);
      return RepositoryResultFactory.success({
        ...data,
        immatriculations,
        taxIdentifiers,
      });
    } catch (error) {
      return RepositoryResultFactory.error(
        toKybException(error as ApiResponse<unknown>),
      );
    }
  }

  /**
   * Relit les immatriculations/identifiants fiscaux déjà persistés (AC-3) : appelée à
   * l'ouverture du dossier, avant tout PATCH/POST, pour que la conformité voie et puisse
   * décider ce qu'un agent — ou elle-même, une session précédente — a déjà déposé.
   */
  async getExistingRegistrations(
    businessKey: string,
  ): Promise<
    RepositoryResult<{
      immatriculations: Immatriculation[];
      taxIdentifiers: TaxIdentifier[];
      /**
       * AC-9 : distingue « jamais rien saisi » de « saisi puis tout retiré ». Les deux donnent une
       * liste vide ci-dessus, mais seul le premier cas doit laisser le préremplissage RCCM du
       * prospect s'afficher côté écran.
       */
      hasEverHadRegistrations: boolean;
    }>
  > {
    try {
      const caseId = await this.resolveCaseId(businessKey);
      const existing = await this.fetchExistingRegistrations(caseId);
      const historyResponse = await this.httpClient.get<ApiResponse<boolean>>({
        url: kybEndpoints.entityRegistrationHistory(caseId),
        withAuth: true,
      });
      const immatriculations: Immatriculation[] = [];
      const taxIdentifiers: TaxIdentifier[] = [];
      for (const dto of existing.values()) {
        if (TAX_IDENTIFIER_ID_TYPES.has(dto.idType)) {
          taxIdentifiers.push({
            id: crypto.randomUUID(),
            registrationId: dto.registrationId,
            type: dto.idType,
            value: dto.idValue,
            country: dto.countryCode,
            status: dto.verificationStatus,
            verification: toVerification(dto),
          });
        } else {
          immatriculations.push({
            id: crypto.randomUUID(),
            registrationId: dto.registrationId,
            registryType: dto.idType,
            number: dto.idValue,
            country: dto.countryCode,
            date: dto.issuedDate ?? undefined,
            status: dto.verificationStatus,
            verification: toVerification(dto),
          });
        }
      }
      return RepositoryResultFactory.success({
        immatriculations,
        taxIdentifiers,
        hasEverHadRegistrations: historyResponse.data,
      });
    } catch (error) {
      return RepositoryResultFactory.error(
        toKybException(error as ApiResponse<unknown>),
      );
    }
  }

  /** AC-9 : retire une immatriculation ou un identifiant fiscal déjà persisté. */
  async removeRegistration(
    businessKey: string,
    registrationId: string,
  ): Promise<RepositoryResult<void>> {
    return executeKybVoidRequest(async () => {
      const caseId = await this.resolveCaseId(businessKey);
      return this.httpClient.delete<ApiResponse<void>>({
        url: kybEndpoints.entityRegistration(caseId, registrationId),
        withAuth: true,
      });
    });
  }

  async getRelationRoles(
    ecran?: KybRelationScreen,
  ): Promise<RepositoryResult<KybRelationRole[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybRelationRoleDto[]>>({
          url: kybEndpoints.relationRoles(ecran),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapRelationRole),
    );
  }

  async listRepresentatives(
    businessKey: string,
    ecran?: KybRelationScreen,
  ): Promise<RepositoryResult<KybRepresentativePerson[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybRepresentativeDto[]>>({
          url: kybEndpoints.representatives(caseId, ecran),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapRepresentative),
    );
  }

  async searchRepresentativeCandidates(
    businessKey: string,
    lastName: string,
    firstName: string,
    birthDate?: string,
  ): Promise<RepositoryResult<KybPersonCandidate[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybPersonCandidateDto[]>>({
          url: kybEndpoints.representativeCandidates(
            caseId,
            lastName,
            firstName,
            birthDate,
          ),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapPersonCandidate),
    );
  }

  /**
   * AC-2 : une réponse 409 porteuse de candidats n'est pas une erreur système — elle est
   * remontée comme un résultat `success` de forme `{ outcome: "duplicate" }`, à charge de
   * l'écran d'en faire quelque chose. `executeRequest` ne s'y prête pas (il traite tout rejet
   * comme un échec) : cette méthode gère donc elle-même son essai/rattrapage.
   */
  async addRepresentative(
    businessKey: string,
    command: AddRepresentativeCommand,
    ecran?: KybRelationScreen,
  ): Promise<RepositoryResult<AddRepresentativeOutcome>> {
    try {
      const caseId = await this.resolveCaseId(businessKey);
      const response = await this.httpClient.post<
        ApiResponse<KybRepresentativeDto>,
        AddRepresentativeCommand
      >({
        url: kybEndpoints.representatives(caseId, ecran),
        body: command,
        withAuth: true,
      });
      return RepositoryResultFactory.success({
        outcome: "added",
        representative: mapRepresentative(response.data),
      });
    } catch (error) {
      const candidates = extractDuplicateCandidates(error);
      if (candidates) {
        return RepositoryResultFactory.success({
          outcome: "duplicate",
          candidates: candidates.map(mapPersonCandidate),
        });
      }
      return RepositoryResultFactory.error(
        toKybException(error as ApiResponse<unknown>),
      );
    }
  }

  async addRepresentativeRole(
    businessKey: string,
    relatedPersonId: string,
    roleCode: string,
    ecran?: KybRelationScreen,
  ): Promise<RepositoryResult<KybPersonRole>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybPersonRoleDto>,
          { roleCode: string }
        >({
          url: kybEndpoints.representativeRoles(caseId, relatedPersonId, ecran),
          body: { roleCode },
          withAuth: true,
        });
      },
      mapPersonRole,
    );
  }

  async listRepresentativeRoles(
    businessKey: string,
    relatedPersonId: string,
  ): Promise<RepositoryResult<KybPersonRole[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybPersonRoleDto[]>>({
          url: kybEndpoints.representativeRoles(caseId, relatedPersonId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapPersonRole),
    );
  }

  async removeRepresentativeRole(
    businessKey: string,
    relatedPersonId: string,
    personRoleId: string,
  ): Promise<RepositoryResult<void>> {
    return executeKybVoidRequest(async () => {
      const caseId = await this.resolveCaseId(businessKey);
      return this.httpClient.delete<ApiResponse<void>>({
        url: kybEndpoints.representativeRole(
          caseId,
          relatedPersonId,
          personRoleId,
        ),
        withAuth: true,
      });
    });
  }

  async addLegalParty(
    businessKey: string,
    command: AddLegalPartyCommand,
  ): Promise<RepositoryResult<KybLegalParty>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybLegalPartyDto>,
          AddLegalPartyCommand
        >({
          url: kybEndpoints.ownershipLegalParties(caseId),
          body: command,
          withAuth: true,
        });
      },
      mapLegalParty,
    );
  }

  async listLegalParties(
    businessKey: string,
  ): Promise<RepositoryResult<KybLegalParty[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybLegalPartyDto[]>>({
          url: kybEndpoints.ownershipLegalParties(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapLegalParty),
    );
  }

  async addOwnershipEdge(
    businessKey: string,
    command: AddOwnershipEdgeCommand,
  ): Promise<RepositoryResult<KybOwnershipEdge>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybOwnershipEdgeDto>,
          AddOwnershipEdgeCommand
        >({
          url: kybEndpoints.ownershipEdges(caseId),
          body: command,
          withAuth: true,
        });
      },
      mapOwnershipEdge,
    );
  }

  async listOwnershipEdges(
    businessKey: string,
  ): Promise<RepositoryResult<KybOwnershipEdge[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybOwnershipEdgeDto[]>>({
          url: kybEndpoints.ownershipEdges(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapOwnershipEdge),
    );
  }

  async removeOwnershipEdge(
    businessKey: string,
    ownershipEdgeId: string,
  ): Promise<RepositoryResult<void>> {
    return executeKybVoidRequest(async () => {
      const caseId = await this.resolveCaseId(businessKey);
      return this.httpClient.delete<ApiResponse<void>>({
        url: kybEndpoints.ownershipEdge(caseId, ownershipEdgeId),
        withAuth: true,
      });
    });
  }

  async getDirectorFallback(
    businessKey: string,
  ): Promise<RepositoryResult<KybDirectorFallback | null>> {
    try {
      const caseId = await this.resolveCaseId(businessKey);
      const response = await this.httpClient.get<ApiResponse<KybDirectorFallbackDto>>({
        url: kybEndpoints.directorFallback(caseId),
        withAuth: true,
      });
      return RepositoryResultFactory.success(mapDirectorFallback(response.data));
    } catch (error) {
      // Aucun repli en cours : état normal quand le dossier a des bénéficiaires effectifs.
      if ((error as { type?: unknown } | null)?.type === DefaultHttpExceptionType.NOT_FOUND) {
        return RepositoryResultFactory.success(null);
      }
      return RepositoryResultFactory.error(
        toKybException(error as ApiResponse<unknown>),
      );
    }
  }

  async saveDirectorFallback(
    businessKey: string,
    relatedPersonId: string,
    justification: string,
  ): Promise<RepositoryResult<KybDirectorFallback>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<KybDirectorFallbackDto>,
          { relatedPersonId: string; justification: string }
        >({
          url: kybEndpoints.directorFallback(caseId),
          body: { relatedPersonId, justification },
          withAuth: true,
        });
      },
      mapDirectorFallback,
    );
  }

  async submitDirectorFallback(
    businessKey: string,
  ): Promise<RepositoryResult<KybDirectorFallback>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<ApiResponse<KybDirectorFallbackDto>, undefined>({
          url: kybEndpoints.directorFallbackSubmit(caseId),
          withAuth: true,
        });
      },
      mapDirectorFallback,
    );
  }

  async computeBeneficialOwners(
    businessKey: string,
  ): Promise<RepositoryResult<KybBeneficialOwner[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybBeneficialOwnerDto[]>>({
          url: kybEndpoints.ownershipBeneficialOwners(caseId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapBeneficialOwner),
    );
  }

  async assessOpacity(
    businessKey: string,
  ): Promise<RepositoryResult<KybOpacityAssessment>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybOpacityAssessmentDto>>({
          url: kybEndpoints.ownershipOpacity(caseId),
          withAuth: true,
        });
      },
      mapOpacityAssessment,
    );
  }

  async exportOwnershipGraph(
    businessKey: string,
    format: KybGraphExportFormat,
  ): Promise<RepositoryResult<KybGraphExportFile>> {
    try {
      const caseId = await this.resolveCaseId(businessKey);
      const base = String(new ViteEnv().get(EnvKeys.BACKEND_BASE_URL) ?? "").replace(/\/$/, "");
      const path = kybEndpoints.ownershipExport(caseId, format);
      const token = useAuthStore.getState().user?.token;
      // Le HttpClient ne rend pas de binaire : même patron que les autres téléchargements, un fetch avec le jeton.
      const response = await fetch(base ? `${base}/${path.replace(/^\//, "")}` : path, {
        method: "GET",
        headers: token ? { "X-App-Token": `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        return RepositoryResultFactory.error(await exportException(response));
      }
      if (format === "PDF") {
        return RepositoryResultFactory.success({
          fileName: fileNameOf(response.headers.get("Content-Disposition"), `graphe-detention-${caseId}.pdf`),
          mimeType: "application/pdf",
          blob: await response.blob(),
        });
      }
      const body = (await response.json()) as ApiResponse<unknown>;
      return RepositoryResultFactory.success({
        fileName: `graphe-detention-${caseId}-${timestamp()}.json`,
        mimeType: "application/json",
        blob: new Blob([JSON.stringify(body.data, null, 2)], { type: "application/json" }),
      });
    } catch (error) {
      return RepositoryResultFactory.error(toKybException(error as ApiResponse<unknown>));
    }
  }

  async checkActiveSignatoryStatus(
    _existingCustomerId: string,
  ): Promise<RepositoryResult<{ isActiveSignatory: boolean }>> {
    // NOSONAR S1135 — no real active-signatory-status endpoint exists yet.
    // Once it ships: replace this branch with an executeRequest call against
    // the real endpoint. Nothing else
    // changes, since KybRepository's interface doesn't change.
    return RepositoryResultFactory.errorWithoutException(
      "Active signatory status endpoint not implemented yet.",
    );
  }

  async generateDocumentChecklist(
    businessKey: string,
    _legalForm: string,
    _incorporationCountry: string,
  ): Promise<RepositoryResult<DocumentChecklist>> {
    // Le back construit la checklist au premier appel puis rend l'état enregistré : générer et relire sont le même appel.
    return this.refreshDocumentChecklist(businessKey);
  }

  async listDocumentTypeLabels(): Promise<RepositoryResult<Record<string, string>>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<{ items?: { typeid: string; libelle?: string | null }[] | null }>>({
          url: kybEndpoints.documentTypes(),
          withAuth: true,
        }),
      (dto) =>
        Object.fromEntries(
          (dto.items ?? []).filter((item) => item.typeid && item.libelle).map((item) => [item.typeid, item.libelle as string]),
        ),
    );
  }

  async refreshDocumentChecklist(
    businessKey: string,
  ): Promise<RepositoryResult<DocumentChecklist>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybCaseDocumentChecklistDto>>({
          url: kybEndpoints.documentChecklist(caseId),
          withAuth: true,
        });
      },
      (dto) => mapChecklist(businessKey, dto),
    );
  }

  /**
   * Dépose une pièce au niveau du dossier (écran 05). Un second dépôt du même `typePieceCode`
   * REMPLACE le précédent (sauf pièce déjà `VALIDE`, 409 dans ce cas) — géré côté back, rien à
   * gérer ici.
   */
  async uploadCaseDocument(
    businessKey: string,
    typePieceCode: string,
    file: File,
  ): Promise<RepositoryResult<CaseDocumentUploadResult>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        const formData = new FormData();
        formData.append("typePieceCode", typePieceCode);
        formData.append("file", file);
        return this.httpClient.post<ApiResponse<KybCaseDocumentUploadDto>, FormData>({
          url: kybEndpoints.documentUpload(caseId),
          body: formData,
          withAuth: true,
        });
      },
      (dto) => ({
        personDocumentId: dto.personDocumentId,
        typePieceCode: dto.typePieceCode,
        fileName: dto.fileName,
      }),
    );
  }

  /** Rattache la pièce déposée (`uploadCaseDocument`) à l'exigence, et fixe le statut de la checklist. */
  async updateCaseDocumentStatus(
    businessKey: string,
    caseDocumentId: string,
    statut: CaseDocumentStatus,
    documentId?: string,
  ): Promise<RepositoryResult<DocumentChecklistItem>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.put<
          ApiResponse<KybCaseDocumentDto>,
          { statut: CaseDocumentStatus; documentId?: string }
        >({
          url: kybEndpoints.documentChecklistItem(caseId, caseDocumentId),
          body: { statut, documentId },
          withAuth: true,
        });
      },
      mapChecklistItem,
    );
  }

  /** AC-3/AC-P1 : le fichier est envoyé tel quel, le SHA-256 est calculé côté serveur. */
  async uploadRegistrationProof(
    businessKey: string,
    registrationId: string,
    file: File,
  ): Promise<RepositoryResult<RegistrationProofUploadResult>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        const formData = new FormData();
        formData.append("file", file);
        return this.httpClient.post<
          ApiResponse<KybEntityRegistrationProofDto>,
          FormData
        >({
          url: kybEndpoints.entityRegistrationProof(caseId, registrationId),
          body: formData,
          withAuth: true,
        });
      },
      (dto) => ({
        proofDocumentId: dto.proofDocumentId,
        sha256: dto.sha256,
        fileName: dto.fileName,
      }),
    );
  }

  /** AC-2/AC-3/AC-5 : la décision porte le `proofDocumentId`/`proofSha256` reçus de l'upload, jamais recalculés. */
  async relaunchPersonScreening(
    businessKey: string,
    relatedPersonId: string,
  ): Promise<RepositoryResult<ScreeningRelaunchOutcome>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<ApiResponse<{ outcome: ScreeningRelaunchOutcome }>, Record<string, never>>({
          url: kybEndpoints.relatedPersonScreeningRelaunch(caseId, relatedPersonId),
          body: {},
          withAuth: true,
        });
      },
      (dto) => dto.outcome,
    );
  }

  async relaunchPendingScreenings(businessKey: string): Promise<RepositoryResult<ScreeningRelaunchSummary>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<ApiResponse<ScreeningRelaunchSummary>, Record<string, never>>({
          url: kybEndpoints.screeningRelaunchPending(caseId),
          body: {},
          withAuth: true,
        });
      },
      (dto) => ({ screened: dto.screened, stillPending: dto.stillPending, failed: dto.failed }),
    );
  }

  async getCaseDocumentUrl(businessKey: string, documentId: string): Promise<RepositoryResult<string>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<{ url: string }>>({
          url: kybEndpoints.documentDownload(caseId, documentId),
          withAuth: true,
        });
      },
      (dto) => dto.url,
    );
  }

  async getRegistrationProofUrl(
    businessKey: string,
    registrationId: string,
    proofDocumentId: string,
  ): Promise<RepositoryResult<string>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<{ url: string }>>({
          url: kybEndpoints.entityRegistrationProofDownload(caseId, registrationId, proofDocumentId),
          withAuth: true,
        });
      },
      (dto) => dto.url,
    );
  }

  async decideRegistrationVerification(
    businessKey: string,
    registrationId: string,
    decision: RegistrationVerificationDecisionInput,
  ): Promise<RepositoryResult<RegistrationVerificationDecisionResult>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        // Le back nomme le statut visé `verificationStatus` : l'envoyer sous `targetStatus` est refusé (400).
        const { targetStatus, ...rest } = decision;
        return this.httpClient.post<
          ApiResponse<KybEntityRegistrationVerifyResponseDto>,
          Omit<RegistrationVerificationDecisionInput, "targetStatus"> & {
            verificationStatus: RegistrationVerificationDecisionInput["targetStatus"];
          }
        >({
          url: kybEndpoints.entityRegistrationVerification(
            caseId,
            registrationId,
          ),
          body: { ...rest, verificationStatus: targetStatus },
          withAuth: true,
        });
      },
      (dto) => ({
        status: dto.verificationStatus,
        verificationSource: dto.verificationSource ?? undefined,
        verifiedBy: dto.verifiedBy ?? undefined,
        verifiedAt: dto.verifiedAt ?? undefined,
      }),
    );
  }

  async saveRepresentativeContact(
    businessKey: string,
    relatedPersonId: string,
    input: SaveContactInput,
  ): Promise<RepositoryResult<SaveContactOutcome>> {
    try {
      const caseId = await this.resolveCaseId(businessKey);
      const response = await this.httpClient.put<
        ApiResponse<KybRepresentativeDto | KybContactChangeDto>,
        SaveContactInput
      >({
        url: kybEndpoints.representativeContact(caseId, relatedPersonId),
        body: input,
        withAuth: true,
      });
      // 202 : le back répond le code "02" avec la demande ; le client générique ne connaît que "00" et "01".
      if (response.code === "02") {
        return RepositoryResultFactory.success({
          outcome: "pending",
          request: mapContactChange(response.data as KybContactChangeDto),
        });
      }
      return RepositoryResultFactory.success({
        outcome: "applied",
        representative: mapRepresentative(response.data as KybRepresentativeDto),
      });
    } catch (error) {
      // Le client HTTP lève tout code autre que « 00 » : un 202 (code « 02 ») arrive donc ici, avec la demande dans le corps.
      const body = (error as { body?: { code?: string; data?: KybContactChangeDto } } | null)?.body;
      if (body?.code === "02" && body.data) {
        return RepositoryResultFactory.success({ outcome: "pending", request: mapContactChange(body.data) });
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async listContactChanges(
    businessKey: string,
    relatedPersonId: string,
  ): Promise<RepositoryResult<KybContactChangeRequest[]>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.get<ApiResponse<KybContactChangeDto[]>>({
          url: kybEndpoints.contactChanges(caseId, relatedPersonId),
          withAuth: true,
        });
      },
      (dtos) => dtos.map(mapContactChange),
    );
  }

  async requestContactChangeMfa(
    businessKey: string,
    relatedPersonId: string,
  ): Promise<RepositoryResult<KybContactMfaChallenge>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<
          ApiResponse<{ sentTo: string; ttlMinutes: number; testCode?: string }>,
          undefined
        >({
          url: kybEndpoints.contactChangeMfaChallenge(caseId, relatedPersonId),
          withAuth: true,
        });
      },
      (dto) => ({ sentTo: dto.sentTo, ttlMinutes: dto.ttlMinutes, testCode: dto.testCode }),
    );
  }

  async approveContactChange(
    businessKey: string,
    relatedPersonId: string,
    requestId: string,
    input: ApproveContactChangeInput,
  ): Promise<RepositoryResult<KybContactChangeRequest>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<ApiResponse<KybContactChangeDto>, ApproveContactChangeInput>({
          url: kybEndpoints.contactChangeApproval(caseId, relatedPersonId, requestId),
          body: input,
          withAuth: true,
        });
      },
      mapContactChange,
    );
  }

  async rejectContactChange(
    businessKey: string,
    relatedPersonId: string,
    requestId: string,
    input: RejectContactChangeInput,
  ): Promise<RepositoryResult<KybContactChangeRequest>> {
    return executeKybRequest(
      async () => {
        const caseId = await this.resolveCaseId(businessKey);
        return this.httpClient.post<ApiResponse<KybContactChangeDto>, RejectContactChangeInput>({
          url: kybEndpoints.contactChangeRejection(caseId, relatedPersonId, requestId),
          body: input,
          withAuth: true,
        });
      },
      mapContactChange,
    );
  }
}
